import React, { useState } from "react";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Bucket } from "../../../api/config/apiConfig";
import letterHeadLeft from "../../../imgs/letter-head-left.png";
import logoFinal from "../../../imgs/logo-final.png";

const CompressedImage = ({ src, alt, style }) => {
  const [dataUrl, setDataUrl] = React.useState(null);

  React.useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.crossOrigin = "Anonymous"; 
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      const MAX_WIDTH = 400;
      let width = img.width;
      let height = img.height;

      if (width > MAX_WIDTH) {
        height = Math.round((height * MAX_WIDTH) / width);
        width = MAX_WIDTH;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Reduce quality to 60% JPEG to keep PDF size small
      const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.6);
      setDataUrl(compressedDataUrl);
    };
    img.onerror = () => {
      // Fallback to original src if CORS fails
      setDataUrl(src);
    };
    img.src = src;
  }, [src]);

  return <img src={dataUrl || src} alt={alt || ""} style={style} />;
};

const SurveyPreviewDownload = ({ surveyData }) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const convertDate = (unixTimestamp) => {
    if (!unixTimestamp) return "-";
    const date = new Date(unixTimestamp * 1000);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    const ordinalSuffix = getOrdinalSuffix(day);
    return `${day}${ordinalSuffix} ${month}, ${year}`;
  };

  let totalWeight = 0;
  let totalCbm = 0;
  let totalItems = 0;

  let totalNotMovingCbm = 0;
  let totalNotMovingItems = 0;
  let totalPackingCbm = 0;
  let totalPackingItems = 0;

  const packingSummary = {};

  surveyData?.surveyDetail?.surveyResult?.surveyRooms?.forEach(room => {
    room?.packingMaterial?.forEach(pack => {
      if (packingSummary[pack.name]) packingSummary[pack.name] += pack.quantity;
      else packingSummary[pack.name] = pack.quantity;
      totalPackingCbm += (pack.cbm || 0);
      totalPackingItems += pack.quantity;
    });
    room?.roomItems?.forEach(item => {
      if (item.type === 'nonmovable') {
        totalNotMovingCbm += (item.cbm || 0);
        totalNotMovingItems += item.quantity;
      }
    });
  });

  surveyData?.roomWiseCalculation?.forEach((cat) => {
    totalWeight += (cat?.totalWeightKg || 0);
    totalCbm += (cat?.totalCbm || 0);
    totalItems += (cat?.totalItems || 0);
  });

  const circleColors = ['#eab308', '#ec4899', '#8b5cf6', '#84cc16', '#f59e0b', '#3b82f6', '#06b6d4', '#ef4444'];

  return (
    <div className="premium-print-container" style={{ fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif', color: '#334155', backgroundColor: '#ffffff' }}>
      {/* 1. Executive Summary Page */}
      <div style={{ pageBreakAfter: 'always', paddingBottom: '40px' }}>
        
        <div style={{ height: '32px' }}></div>
        {/* Client & Logistics Details Grid (Including Description) */}
        <div style={{ padding: '0 40px', marginBottom: '32px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          <div style={{ display: 'flex' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Customer Name:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.name || "-"}</strong></div>
          <div style={{ display: 'flex' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Survey ID:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.uuid?.slice(0, 8)?.toUpperCase() || "-"}</strong></div>
          <div style={{ display: 'flex' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Survey Date:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.surveyDate ? formatDate(convertDate(surveyData?.surveyDetail?.surveyDate)) : "-"}</strong></div>
          <div style={{ display: 'flex' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Phone:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.phone || "-"}</strong></div>
          <div style={{ display: 'flex' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Email:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.email || "-"}</strong></div>
          <div style={{ display: 'flex' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Van No:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.vanNo || "-"}</strong></div>
          <div style={{ display: 'flex', gridColumn: 'span 2' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Origin:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.loadPlaceStAddress ? `${surveyData.surveyDetail.loadPlaceStAddress}, ${surveyData.surveyDetail.loadPlaceCity}` : "-"}</strong></div>
          <div style={{ display: 'flex', gridColumn: 'span 2' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Destination:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.unloadPlaceStAddress ? `${surveyData.surveyDetail.unloadPlaceStAddress}, ${surveyData.surveyDetail.unloadPlaceCity}` : "-"}</strong></div>
          <div style={{ display: 'flex', gridColumn: 'span 2' }}><span style={{ color: '#64748b', width: '120px', fontSize: '9.5pt' }}>Description:</span> <strong style={{ color: '#0f172a', fontSize: '9.5pt' }}>{surveyData?.surveyDetail?.description || "-"}</strong></div>
        </div>

        {/* Executive Summary Body */}
        <div style={{ display: 'flex', gap: '24px', padding: '0 40px' }}>
          {/* Left Column */}
          <div style={{ flex: '0 0 35%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Packing Materials Box */}
            <div style={{ border: '1px solid #cbd5e1', padding: '20px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '11pt', color: '#0f172a', fontWeight: 700, textTransform: 'uppercase' }}>Packing Materials</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(packingSummary).map(([name, qty], i) => (
                  <div key={i} style={{ display: 'flex', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden', fontSize: '9pt', border: '1px solid #e2e8f0' }}>
                    <div style={{ padding: '4px 8px', color: '#334155' }}>{name}</div>
                    <div style={{ padding: '4px 8px', backgroundColor: '#0f172a', color: 'white', fontWeight: 600 }}>{qty}</div>
                  </div>
                ))}
                {Object.keys(packingSummary).length === 0 && <div style={{ fontSize: '9pt', color: '#94a3b8' }}>None specified</div>}
              </div>
              {Object.keys(packingSummary).length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden', fontSize: '9pt', fontWeight: 600 }}>
                  <div style={{ padding: '4px 8px', flex: 1, color: '#334155' }}>Total CP Pack Count</div>
                  <div style={{ padding: '4px 8px', backgroundColor: '#0f172a', color: 'white' }}>{totalPackingItems}</div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Table */}
          <div style={{ flex: '1' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt', border: '1px solid #cbd5e1' }}>
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#0f172a', color: 'white', padding: '12px', textAlign: 'left', fontWeight: 600 }}>ROOM</th>
                  <th style={{ backgroundColor: '#0f172a', color: 'white', padding: '12px', textAlign: 'right', fontWeight: 600 }}>VOLUME</th>
                  <th style={{ backgroundColor: '#0f172a', color: 'white', padding: '12px', textAlign: 'right', fontWeight: 600 }}>WEIGHT</th>
                  <th style={{ backgroundColor: '#0f172a', color: 'white', padding: '12px', textAlign: 'center', fontWeight: 600 }}>ITEMS</th>
                </tr>
              </thead>
              <tbody>
                {surveyData?.roomWiseCalculation?.map((cat, index) => (
                  <tr key={index}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{cat?.roomName || "Unspecified"}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155', textAlign: 'right' }}>{(cat?.totalCbm || 0).toFixed(2)} m³</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155', textAlign: 'right' }}>{(cat?.totalWeightKg || 0).toFixed(0)} kg</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155', textAlign: 'center' }}>{cat?.totalItems || 0}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="4" style={{ height: '8px', borderBottom: '1px solid #e2e8f0' }}></td>
                </tr>
                <tr>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: '#0f172a', borderTop: '1px solid #e2e8f0' }}>TOTAL INVENTORY</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: '#0f172a', textAlign: 'right', borderTop: '1px solid #e2e8f0' }}>{totalCbm.toFixed(2)} m³</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: '#0f172a', textAlign: 'right', borderTop: '1px solid #e2e8f0' }}>{totalWeight.toFixed(0)} kg</td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: '#0f172a', textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>{totalItems}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 2. Room Pages */}
      <div className="room-list-container">
        {surveyData?.surveyDetail?.surveyResult?.surveyRooms?.map((sr, ind) => {
          if (!sr) return null;
          const allItems = sr?.roomItems || [];
          
          const roomCalc = surveyData?.roomWiseCalculation?.find(cat => cat?.roomName === sr?.name) || {};
          const roomWeight = roomCalc?.totalWeightKg || 0;
          const roomCbm = roomCalc?.totalCbm || 0;
          const roomItemsCount = sr?.roomItems?.length || 0;

          return (
            <div key={ind} style={{ pageBreakBefore: 'always', paddingBottom: '40px' }}>
              
              {/* Clean Room Header without color block */}
              <div style={{ padding: '24px 40px', borderBottom: '2px solid #e2e8f0', marginBottom: '24px' }}>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '20pt', fontWeight: 700, color: '#0f172a' }}>{sr?.name || `Room ${ind + 1}`}</h2>
                <p style={{ margin: 0, fontSize: '10pt', fontWeight: 500, color: '#64748b' }}>
                  {sr?.floorName || "Ground Level"} &nbsp;&bull;&nbsp; {roomItemsCount} Items &nbsp;&bull;&nbsp; {roomCbm.toFixed(2)} m³ &nbsp;&bull;&nbsp; {roomWeight.toFixed(0)} kg
                </p>
              </div>
              
              {/* Content Body: 2 Columns */}
              <div style={{ display: 'flex', padding: '0 40px', gap: '24px' }}>
                
                {/* Left Column: Images */}
                <div style={{ flex: '0 0 45%' }}>
                  {sr?.images?.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px' }}>
                      {sr?.images?.slice(0, 6)?.map((image, imgInd) => (
                         <CompressedImage key={imgInd} src={Bucket + image} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }} alt="" />
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', border: '1px dashed #cbd5e1', fontSize: '9pt', fontStyle: 'italic' }}>No Images Available</div>
                  )}
                  
                  {sr?.remarks && (
                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8fafc', fontSize: '9pt', color: '#334155', borderLeft: '4px solid #0f172a' }}>
                      <strong>Remarks:</strong> {sr.remarks}
                    </div>
                  )}
                </div>

                {/* Right Column: Tables */}
                <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  
                  {/* All Items Table */}
                  <div style={{ border: '1px solid #cbd5e1' }}>
                    <div style={{ backgroundColor: '#0f172a', color: 'white', padding: '8px 12px', fontSize: '10pt', fontWeight: 600 }}>Room Items</div>
                    {allItems.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
                        <tbody>
                          {allItems.map((item, pi) => (
                            <tr key={`item-${pi}`}>
                              <td style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>
                                <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', border: `3px solid ${circleColors[pi % circleColors.length]}`, marginRight: '10px', verticalAlign: 'middle' }}></span>
                                {item?.name}
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{item?.quantity}x</td>
                              <td style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{(item?.cbm || 0).toFixed(2)} m³</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#0f172a', fontStyle: 'italic', fontSize: '9pt', backgroundColor: '#f8fafc' }}>No Items</div>
                    )}
                  </div>

                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={
            snackbarMessage?.includes("successful") ? "success" : "error"
          }
          onClose={handleCloseSnackbar}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default SurveyPreviewDownload;
